import React, { useEffect } from 'react';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo from '../../../../components/SelectTwo';
import { BatchDataForm } from '../../../../app/models/Batch';

type SelectOpt = { id: number | string; name: string; quality__name?: string | null };

type Props = {
  batchDataForm: BatchDataForm;
  setBatchDataForm: (data: BatchDataForm) => void;
  projects: SelectOpt[];
  centers: SelectOpt[];
  units: SelectOpt[];
  batchCategories: SelectOpt[];
  societies: SelectOpt[];
  warehouses: SelectOpt[];
  products: SelectOpt[];
  disabled?: boolean;
  errorFields?: any;
};

const BatchForm = ({
  batchDataForm,
  setBatchDataForm,
  projects,
  centers,
  units,
  batchCategories,
  societies,
  warehouses,
  products,
  disabled = false,
  errorFields
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

  useEffect(() => { setErrors(errorFields); }, [errorFields]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBatchDataForm({ ...batchDataForm, [name]: value });
  };

  const handleChangeSelectTwo = (name: keyof BatchDataForm, value: number | string | null) => {
    const updates: Partial<BatchDataForm> = { [name]: (value ?? null) as any };
    
    // Si se cambia el producto, actualizar la calidad automáticamente
    if (name === 'product_id') {
      const selectedProduct = products.find(p => p.id === value);
      if (selectedProduct && selectedProduct.quality__name) {
        updates.quality = selectedProduct.quality__name;
      } else {
        updates.quality = '';
      }
    }
    
    setBatchDataForm({ ...batchDataForm, ...updates });
  };

  const mapOptions = (opts: SelectOpt[]) => opts.map(o => ({ value: o.id ?? null, label: `${o.name}` }));

  const onChangeCategory = (v: any) => {
    const newCategory = v?.value ?? null;

    // Resets según lógica de negocio
    let patch: Partial<BatchDataForm> = { batch_category: newCategory as any };
    if (newCategory === 'COLLECTION') {
      patch = { ...patch, product_id: null, quality: '' };
    } else if (newCategory === 'COMMERCIAL') {
      patch = { ...patch, project_id: null };
    }
    setBatchDataForm({ ...batchDataForm, ...patch });
    onFocusRemove('batch_category');
  };

  const isCollection = batchDataForm.batch_category === 'COLLECTION';
  const isSelection = batchDataForm.batch_category === 'SELECTION';
  const isCommercial = batchDataForm.batch_category === 'COMMERCIAL';

  return (
    <>
      <div className="mb-2 row">
        <div className="col-md-12">
          <label className="col-form-label">Sociedad</label>
          <SelectTwo
            name="society_id"
            id="society_id"
            inputValue={batchDataForm.society_id ?? null}
            options={mapOptions(societies)}
            hasError={fieldHasError('society_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('society_id', v?.value)}
            placeholder="Seleccione sociedad"
            onFocus={() => onFocusRemove('society_id')}
            disabled={disabled}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('society_id')}</div>
        </div>

        <div className="col-md-12">
          <label className="col-form-label">Centro</label>
          <SelectTwo
            name="center_id"
            id="center_id"
            inputValue={batchDataForm.center_id ?? null}
            options={mapOptions(centers)}
            hasError={fieldHasError('center_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('center_id', v?.value)}
            placeholder="Seleccione un centro"
            onFocus={() => onFocusRemove('center_id')}
            disabled={disabled}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('center_id')}</div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Almacén</label>
          <SelectTwo
            name="warehouse_id"
            id="warehouse_id"
            inputValue={batchDataForm.warehouse_id ?? null}
            options={mapOptions(warehouses)}
            hasError={fieldHasError('warehouse_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('warehouse_id', v?.value)}
            placeholder="Seleccione almacén"
            onFocus={() => onFocusRemove('warehouse_id')}
            disabled={disabled}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('warehouse_id')}</div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Tipo de Lote</label>
          <SelectTwo
            name="batch_category"
            id="batch_category"
            inputValue={batchDataForm.batch_category ?? null}
            options={mapOptions(batchCategories)}
            hasError={fieldHasError('batch_category') !== ''}
            onChange={onChangeCategory}
            placeholder="Seleccione categoría"
            onFocus={() => onFocusRemove('batch_category')}
            disabled={disabled}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('batch_category')}</div>
        </div>

        <div className="col-md-12">
          <label className="col-form-label">Proyecto</label>
          <SelectTwo
            name="project_id"
            id="project_id"
            inputValue={batchDataForm.project_id ?? null}
            options={mapOptions(projects)}
            hasError={fieldHasError('project_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('project_id', v?.value)}
            placeholder="Seleccione un proyecto"
            onFocus={() => onFocusRemove('project_id')}
            disabled={disabled || isCommercial} // COMMERCIAL no requiere proyecto
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('project_id')}</div>
        </div>

      </div>

      <div className="mb-2 row">
        
        <div className="col-md-12">
          <label className="col-form-label">Producto</label>
          <SelectTwo
            name="product_id"
            id="product_id"
            inputValue={batchDataForm.product_id ?? null}
            options={mapOptions(products)}
            hasError={fieldHasError('product_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('product_id', v?.value)}
            placeholder="Seleccione un producto"
            onFocus={() => onFocusRemove('product_id')}
            disabled={disabled || isCollection} // COLLECTION no requiere producto
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('product_id')}</div>
        </div>

        <div className="col-md-12">
          <label className="col-form-label">Calidad</label>
          <input
            name="quality"
            id="quality"
            type="text"
            className={`form-control ${fieldHasError('quality')}`}
            value={batchDataForm.quality ?? ''}
            readOnly
            placeholder="Se hereda del producto seleccionado"
            disabled={disabled || isCollection || !batchDataForm.product_id} // COLLECTION no requiere calidad, solo lectura cuando hay producto
            style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('quality')}</div>
        </div>

      </div>
          
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Capacidad</label>
          <input
            name="capacity"
            id="capacity"
            type="number"
            className={`form-control ${fieldHasError('capacity')}`}
            value={batchDataForm.capacity ?? 0}
            onChange={handleChange}
            onFocus={() => onFocusRemove('capacity')}
            placeholder="0"
            disabled={disabled}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('capacity')}</div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Unidad</label>
          <SelectTwo
            name="unit_id"
            id="unit_id"
            inputValue={batchDataForm.unit_id ?? null}
            options={mapOptions(units)}
            hasError={fieldHasError('unit_id') !== ''}
            onChange={(v: any) => handleChangeSelectTwo('unit_id', v?.value)}
            placeholder="Seleccione unidad"
            onFocus={() => onFocusRemove('unit_id')}
            disabled={disabled}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('unit_id')}</div>
        </div>

      </div>

    </>
  );
};

export default BatchForm;
